// invoiceSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

export const fetchDraftItems = createAsyncThunk(
  'invoice/fetchDraftItems',
  async (indentId) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/viewTable/indentitems?indentId=${indentId}`
    );
    const data = await response.json();
    
    const indentStatus = data.length > 0 
      ? {
          l1: data[0].L1A || 0,
          l2: data[0].L2 || 0,
          po: data[0].PO || 0,
          ph: data[0].PH || 0,
          so: data[0].IR || 0,
        }
      : { l1: 0, l2: 0, po: 0, ph: 0, so: 0 };

    const items = data.map((item) => ({
      itemID: item.ItemID,
      itemName: item.ItemName,
      quantity: item.Quantity,
      stock: item.Stock,
      categoryID: item.CategoryID,
      remarks: item.Description,
      promisedDate: item.PromisedDate,
      total: item.Quantity * item.Stock,
      accountingUnitID: item.AccountingUnitID,
    }));

    return { items, indentStatus };
  }
);

export const fetchCategories = createAsyncThunk(
  'invoice/fetchCategories',
  async () => {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/dropdown/mcatdrop`);
    return response.json();
  }
);

export const fetchChangeLogs = createAsyncThunk(
  'invoice/fetchChangeLogs',
  async (indentId) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/change_log/change-logs/${indentId}`
    );
    return response.json();
  }
);

export const saveChangeLog = createAsyncThunk(
  'invoice/saveChangeLog',
  async ({ indentId, userId, type, item, original, updated }) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/change_log/change-logs`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          indentId,
          userId,
          currentlevel: 'Indent',
          type,
          item,
          original,
          updated,
        }),
      }
    );
    return response.json();
  }
);

export const saveDraft = createAsyncThunk(
  'invoice/saveDraft',
  async ({ indentId, items }) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/viewTable/indents/${indentId}/draft`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          isDraft: true,
          items: items.map(item => ({
            itemID: item.itemID,
            quantity: item.quantity,
            remarks: item.remarks,
            categoryID: item.categoryID,
            stock: item.stock,
            promisedDate: item.promisedDate,
          })),
        }),
      }
    );
    return response.json();
  }
);

export const submitIndent = createAsyncThunk(
  'invoice/submitIndent',
  async ({ indentId, userId, items }) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/viewTable/indents/submit`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          indentId,
          userId,
          items: items.map(item => ({
            itemID: item.itemID,
            quantity: item.quantity,
            stock: item.stock,
            remarks: item.remarks,
            categoryID: item.categoryID,
            promisedDate: item.promisedDate,
            accountingUnitID: item.accountingUnitID,
          })),
        }),
      }
    );
    return response.json();
  }
);

const invoiceSlice = createSlice({
  name: 'invoice',
  initialState: {
    items: [],
    categories: [],
    changeLogs: [],
    indentStatus: { l1: 0, l2: 0, po: 0, ph: 0, so: 0 },
    isDraft: true,
    isEditable: true,
    loading: false,
    error: null,
    changeLog: {
      addedItems: [],
      deletedItems: [],
      updatedItems: [],
    },
  },
  reducers: {
    addItem: (state, action) => {
      state.items.push(action.payload);
      state.changeLog.addedItems.push(action.payload);
    },
    deleteItem: (state, action) => {
      const deletedItem = state.items[action.payload];
      state.items = state.items.filter((_, index) => index !== action.payload);
      state.changeLog.deletedItems.push(deletedItem);
    },
    updateItem: (state, action) => {
      const { index, item } = action.payload;
      const original = state.items[index];
      state.items[index] = item;
      state.changeLog.updatedItems.push({ original, updated: item });
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDraftItems.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDraftItems.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.indentStatus = action.payload.indentStatus;
      })
      .addCase(fetchDraftItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Add other cases for categories, changelogs, etc.
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(fetchChangeLogs.fulfilled, (state, action) => {
        state.changeLogs = action.payload;
      })
      .addCase(saveDraft.fulfilled, (state) => {
        state.isDraft = true;
      })
      .addCase(submitIndent.fulfilled, (state) => {
        state.isDraft = false;
        state.isEditable = false;
      });
  },
});

export const { addItem, deleteItem, updateItem, setError } = invoiceSlice.actions;
export default invoiceSlice.reducer;